/**
 * 一時的な選択制約設定
 *
 * このファイルで全ての一時的制約を管理します。
 * 制約を解除する場合は、該当のフラグを false に変更してください。
 *
 * 最終更新: 2026-01-13
 */

export interface TemporaryRestrictions {
  /** 裏ラベル「必要」を非活性（全商品共通） */
  disableBackLabelRequired: boolean;
  // フリーズドライ関連
  freezeDried: {
    /** インナーパッケージデザイン「必要」を非活性（スティック用） */
    disableInnerDesignRequired: boolean;
    /** 外箱「必要」を非活性 */
    disableOuterBoxRequired: boolean;
    /** 表ラベルの選択を非活性（キューブ用） */
    disableFrontLabelSelection: boolean;
  };
  // 濃縮液関連
  liquidConcentrate: {
    /** スパウトを非活性 */
    disableSpout: boolean;
    /** 外箱「必要」を非活性 */
    disableOuterBoxRequired: boolean;
    /** 表ラベルの選択を非活性 */
    disableFrontLabelSelection: boolean;
  };
  // ストレートコーヒー関連
  straightCoffee: {
    /** ボトルを非活性 */
    disableBottle: boolean;
    /** 砂糖濃度を全て非活性（無糖をデフォルト設定） */
    disableSugar: boolean;
    /** 外箱「必要」を非活性 */
    disableOuterBoxRequired: boolean;
    /** 表ラベルの選択を非活性 */
    disableFrontLabelSelection: boolean;
  };
}

/**
 * 現在有効な制約設定
 *
 * 制約を解除するには該当フラグを false に変更
 */
export const TEMPORARY_RESTRICTIONS: TemporaryRestrictions = {
  disableBackLabelRequired: true, // 裏ラベル「必要」非活性（全商品共通）
  freezeDried: {
    disableInnerDesignRequired: true, // インナーパッケージデザイン「必要」非活性
    disableOuterBoxRequired: true, // 外箱「必要」非活性
    disableFrontLabelSelection: true, // 表ラベル選択非活性（キューブ用）
  },
  liquidConcentrate: {
    disableSpout: true, // スパウト非活性
    disableOuterBoxRequired: true, // 外箱「必要」非活性
    disableFrontLabelSelection: true, // 表ラベル選択非活性
  },
  straightCoffee: {
    disableBottle: true, // ボトル非活性
    disableSugar: true, // 砂糖濃度を全て非活性
    disableOuterBoxRequired: true, // 外箱「必要」非活性
    disableFrontLabelSelection: true, // 表ラベル選択非活性
  },
};

/**
 * 非活性時に表示するメッセージキー
 * useTranslations で使用
 */
export const RESTRICTION_MESSAGE_KEY = 'temporaryUnavailable_message';

/**
 * 保存データの制約チェック用の型
 */
export interface SavedDataForRestrictionCheck {
  productType: string;
  packageType: string;
  sugarConcentration: string | null;
  needsInnerDesign?: boolean;
  needsFrontLabel?: boolean;
  needsOuterBox: boolean;
  needsBackLabel?: boolean;
}

/**
 * 保存データが一時的制約に引っかかるかチェックする
 *
 * @param data 保存データ
 * @returns true = 制約違反（再開不可）, false = 問題なし
 */
export const checkSavedDataRestrictions = (
  data: SavedDataForRestrictionCheck,
): boolean => {
  const {
    productType,
    packageType,
    sugarConcentration,
    needsInnerDesign,
    needsFrontLabel,
    needsOuterBox,
    needsBackLabel,
  } = data;

  // ストレートコーヒー + ボトル
  if (
    productType === 'straight-coffee' &&
    packageType === 'bottle' &&
    TEMPORARY_RESTRICTIONS.straightCoffee.disableBottle
  ) {
    return true;
  }

  // ストレートコーヒー + 砂糖あり（none以外）
  if (
    productType === 'straight-coffee' &&
    sugarConcentration &&
    sugarConcentration !== 'none' &&
    TEMPORARY_RESTRICTIONS.straightCoffee.disableSugar
  ) {
    return true;
  }

  // 濃縮液 + スパウト
  if (
    productType === 'liquid-concentrate' &&
    packageType === 'spout' &&
    TEMPORARY_RESTRICTIONS.liquidConcentrate.disableSpout
  ) {
    return true;
  }

  // 濃縮液 + 表ラベル
  if (
    productType === 'liquid-concentrate' &&
    needsFrontLabel &&
    TEMPORARY_RESTRICTIONS.liquidConcentrate.disableFrontLabelSelection
  ) {
    return true;
  }

  // ストレートコーヒー + 表ラベル
  if (
    productType === 'straight-coffee' &&
    needsFrontLabel &&
    TEMPORARY_RESTRICTIONS.straightCoffee.disableFrontLabelSelection
  ) {
    return true;
  }

  // フリーズドライ + スティック + インナーデザイン必要
  if (
    productType === 'freeze-dried' &&
    packageType === 'stick' &&
    needsInnerDesign &&
    TEMPORARY_RESTRICTIONS.freezeDried.disableInnerDesignRequired
  ) {
    return true;
  }

  // フリーズドライ + キューブ + 表ラベル必要
  if (
    productType === 'freeze-dried' &&
    packageType === 'cube' &&
    needsFrontLabel &&
    TEMPORARY_RESTRICTIONS.freezeDried.disableFrontLabelSelection
  ) {
    return true;
  }

  // 裏ラベル必要（全商品共通）
  if (needsBackLabel && TEMPORARY_RESTRICTIONS.disableBackLabelRequired) {
    return true;
  }

  // 外箱必要の制約チェック（全商品タイプ共通）
  if (needsOuterBox) {
    if (
      productType === 'freeze-dried' &&
      TEMPORARY_RESTRICTIONS.freezeDried.disableOuterBoxRequired
    ) {
      return true;
    }
    if (
      productType === 'liquid-concentrate' &&
      TEMPORARY_RESTRICTIONS.liquidConcentrate.disableOuterBoxRequired
    ) {
      return true;
    }
    if (
      productType === 'straight-coffee' &&
      TEMPORARY_RESTRICTIONS.straightCoffee.disableOuterBoxRequired
    ) {
      return true;
    }
  }

  return false;
};
